import { useCallback, useEffect, useState } from "react";
import type { Row } from "../types";
import { api, ApiError, type PlanoAcaoItem } from "../api";
import { IconClose, IconSparkles } from "../icons";

interface Plano {
  causa_raiz: string;
  acoes: PlanoAcaoItem[];
  observacoes: string;
  fonte: "ia" | "padrao";
}

interface Props {
  nc: Row;
  onClose: () => void;
  onApplied: () => void;
}

/**
 * Plano de ação por IA para correção de uma Não Conformidade.
 * Gera (OpenAI ou plano padrão) causa raiz + ações; ao aplicar, cria as ações
 * na aba "Ações Corretivas" vinculadas à NC.
 */
export default function PlanoAcaoModal({ nc, onClose, onApplied }: Props) {
  const [plano, setPlano] = useState<Plano | null>(null);
  const [gestor, setGestor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [aplicando, setAplicando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [criadas, setCriadas] = useState(0);
  const [jaAplicado, setJaAplicado] = useState(false); // trava re-aplicar (mesmo após Regerar)

  const gerar = useCallback(async () => {
    setLoading(true);
    setError(null);
    setCriadas(0);
    try {
      const r = await api.planoAcao.gerar(nc.id);
      setPlano(r.plano);
      setGestor(r.gestor);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Erro ao gerar o plano");
    } finally {
      setLoading(false);
    }
  }, [nc.id]);

  useEffect(() => {
    gerar();
  }, [gerar]);

  async function aplicar() {
    if (!plano || jaAplicado) return;
    setAplicando(true);
    setError(null);
    try {
      const r = await api.planoAcao.aplicar(nc.id, plano.acoes, plano.causa_raiz);
      setCriadas(r.criadas);
      setJaAplicado(true);
      onApplied();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Erro ao criar as ações");
    } finally {
      setAplicando(false);
    }
  }

  const codigo = nc.codigo ? `${nc.codigo} · ` : "";

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card modal-wide" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>
            <IconSparkles size={18} /> Plano de ação — {codigo}
            {nc.titulo}
          </h3>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body">
          <div className="plano-ctx">
            <span>{nc.departamento || "Sem departamento"}</span>
            {gestor && (
              <span>
                Gestor do setor: <strong>{gestor}</strong>
              </span>
            )}
            {nc.gravidade && <span className="plano-grav">Gravidade: {nc.gravidade}</span>}
          </div>

          {loading ? (
            <div className="empty">Gerando plano de ação com IA…</div>
          ) : !plano ? (
            <div className="empty">Não foi possível gerar o plano.</div>
          ) : (
            <>
              <div className={`plano-fonte ${plano.fonte}`}>
                {plano.fonte === "ia" ? "✦ Gerado por IA" : "Plano padrão (IA indisponível no momento)"}
              </div>

              <div className="plano-bloco">
                <h4>Causa raiz provável</h4>
                <p>{plano.causa_raiz}</p>
              </div>

              <div className="plano-bloco">
                <h4>Ações propostas ({plano.acoes.length})</h4>
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Ação</th>
                        <th>Tipo</th>
                        <th>Responsável</th>
                        <th>Prazo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {plano.acoes.map((a, i) => (
                        <tr key={i}>
                          <td>{a.descricao}</td>
                          <td>{a.tipo}</td>
                          <td>{a.responsavel}</td>
                          <td className="nowrap">{a.prazo_dias} dias</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {plano.observacoes && <p className="muted plano-obs">{plano.observacoes}</p>}

              {criadas > 0 && (
                <div className="plano-ok">
                  ✓ {criadas} ação(ões) criada(s) na aba “Ações Corretivas”, vinculada(s) a esta NC.
                </div>
              )}
            </>
          )}

          {error && <div className="form-error">{error}</div>}
        </div>

        <div className="modal-foot">
          <button type="button" className="btn-secondary" onClick={gerar} disabled={loading || aplicando}>
            Regerar
          </button>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Fechar
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={aplicar}
            disabled={loading || aplicando || !plano || jaAplicado}
          >
            {aplicando ? "Criando..." : jaAplicado ? "Ações criadas ✓" : "Criar ações corretivas"}
          </button>
        </div>
      </div>
    </div>
  );
}
